/*
Copyright (c) 2024, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/

import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import VSlider from '../veneer/slider/slider';
import './styles.scss';
import { withAnalytics } from '@veneer/analytics';
import { useDirection } from '@veneer/core';
import { SliderProps } from './types';
import { generateID } from '../../base/helper';

/**
 * UI Toolkit Slider component based on Veneer Slider
 * @public
 */
const TSlider: React.FC<SliderProps> = ({
  leadingIcon,
  trailingIcon,
  orientation = 'horizontal',
  onChange,
  onChangeEnd,
  onDragEnd,
  onDragStart,
  disabled,
  isRTLDisabled = false,
  id = generateID('clientos-ui-toolkit-slider'),
  ...rest
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [value, setValue] = useState(rest.value);
  const valueRef = useRef(value);

  const isRTL = useDirection().isRTL;

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    if (onDragStart) {
      onDragStart(event.nativeEvent);
    }
  };

  const handleKeyUp = (event: React.KeyboardEvent) => {
    if (
      ['ArrowUp', 'ArrowRight', 'ArrowDown', 'ArrowLeft'].includes(event.key)
    ) {
      if (onChangeEnd) {
        onChangeEnd(valueRef.current);
      }
    }
  };

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    setIsDragging(true);
    if (onDragStart) {
      onDragStart(event.nativeEvent);
    }
  };

  const handleOnChange = (value: number) => {
    setValue(value);
    if (onChange) {
      onChange(value);
    }
  };

  const handleDragEnd = useCallback(
    (event?: MouseEvent | TouchEvent) => {
      setIsDragging(false);
      setTimeout(() => {
        if (onDragEnd) onDragEnd(event);
        if (onChangeEnd) onChangeEnd(valueRef.current);
      }, 10);
    },
    [onChangeEnd, onDragEnd]
  );

  useEffect(() => {
    const handleGlobalPointerUp = (event: PointerEvent) => {
      if (isDragging) {
        handleDragEnd(event);
      }
    };

    document.addEventListener('pointerup', handleGlobalPointerUp);

    return () => {
      document.removeEventListener('pointerup', handleGlobalPointerUp);
    };
  }, [handleDragEnd, isDragging]);

  const sliderClass = `clientos-ui-toolkit-slider__slider ${disabled ? 'disabled' : ''} ${
    isRTLDisabled && isRTL ? 'no-rtl' : ''
  }`;

  return (
    <div
      className={`clientos-ui-toolkit-slider ${orientation} ${isRTLDisabled && isRTL ? 'no-rtl' : ''}`}
    >
      {leadingIcon && (
        <div
          className={`clientos-ui-toolkit-slider__leading-icon `}
          id={`${id}__leading-icon`}
        >
          {leadingIcon}
        </div>
      )}
      <div
        className={sliderClass}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onKeyUp={handleKeyUp}
      >
        <div
          className={`clientos-ui-toolkit-slider__wrapper ${!isRTLDisabled && isRTL && orientation == 'horizontal' ? 'rtl' : ''}`}
        >
          <VSlider
            orientation={orientation}
            onChange={handleOnChange}
            disabled={disabled}
            id={id}
            isRTL={!isRTLDisabled && isRTL}
            {...rest}
          />
        </div>
      </div>
      {trailingIcon && (
        <div
          className={`clientos-ui-toolkit-slider__trailing-icon`}
          id={`${id}__trailing-icon`}
        >
          {trailingIcon}
        </div>
      )}
    </div>
  );
};

export const SliderAnalytics = withAnalytics(TSlider);
export default memo(TSlider);
